import React from 'react';
import { GatsbyImage } from 'gatsby-plugin-image';
import 'swiper/css/autoplay';
import 'swiper/css/pagination';
import styled from 'styled-components';
import {
  breakpointSizes,
  minBreakpointQuery,
  maxBreakpointQuery,
  sectionMargins,
  standardColours,
  brandColours,
  elementColours,
  fontWeights,
  fluidFontSize,
  boxShadow,
} from '../styles';
import { Container, Button } from './ui';
import BannerCta from './BannerCta';

const StyledBanner = styled.section`
  ${sectionMargins('20px', '40px')};
`;

const StyledInner = styled.div`
  display: grid;
  gap: 20px;

  ${minBreakpointQuery.large`
    grid-template-columns: 2.2fr 1fr;
  `}

  ${minBreakpointQuery.xxxxlarge`
    gap: 25px;
  `}
`;

const StyledPrimary = styled.article`
  position: relative;
  display: grid;
  min-height: 450px;
  ${boxShadow()};
  border-radius: 15px;
  overflow: hidden;

  ${minBreakpointQuery.small`
    min-height: 480px;
  `}

  ${minBreakpointQuery.large`
    min-height: 510px;
  `}

  ${minBreakpointQuery.xxxxlarge`
    min-height: 540px;
  `}
`;

const StyledImage = styled(GatsbyImage)`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
`;

const StyledContent = styled.div`
  position: relative;
  margin-top: auto;
  padding: 30px;
  background: linear-gradient(
    ${standardColours.transparent},
    ${brandColours.quinary}
  );

  ${minBreakpointQuery.small`
    padding: 40px;
  `}

  ${minBreakpointQuery.large`
    padding: 50px;
  `}

  ${minBreakpointQuery.xxxxlarge`
    padding: 60px;
  `}
`;

const StyledHeading = styled.h1`
  max-width: 520px;
  color: ${standardColours.white};
  font-weight: ${fontWeights.semibold};
  ${fluidFontSize(
    '32px',
    '64px',
    breakpointSizes.tiny,
    breakpointSizes.xxxxlarge,
  )};
  line-height: 1;
`;

const StyledButtons = styled.div`
  --columns: 1;
  --gap: 10px;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: var(--gap);
  margin-top: 20px;

  ${minBreakpointQuery.tiny`
    --columns: 2;
  `}

  ${minBreakpointQuery.small`
    --gap: 15px;
    margin-top: 25px;
  `}

  ${minBreakpointQuery.medium`
    --columns: 4;
    margin-top: 30px;
  `}

  ${minBreakpointQuery.large`
    --gap: 20px;
    margin-top: 35px;
  `}

  ${minBreakpointQuery.xxlarge`
    --gap: 25px;
    margin-top: 40px;
  `}
`;

const StyledButton = styled(Button)`
  display: flex;
  justify-content: center;
  align-items: center;
  width: calc((100% - (var(--columns) - 1) * var(--gap)) / var(--columns));
  text-align: center;
  color: ${elementColours.textColour};
  background-color: ${standardColours.white};

  ${maxBreakpointQuery.tiny`
    padding-top: 10px;
    padding-bottom: 10px;
  `}

  &:hover {
    color: ${standardColours.white};
  }
`;

const StyledSecondary = styled.div`
  display: grid;
  gap: 20px;

  ${minBreakpointQuery.smedium`
    grid-template-columns: repeat(2, 1fr);
  `}

  ${minBreakpointQuery.large`
    grid-template-columns: none;
  `}
`;

const Banner = ({ heading, links, image, ctas }) => (
  <StyledBanner>
    <Container wide={true}>
      <StyledInner>
        <StyledPrimary>
          <StyledImage
            image={image.gatsbyImageData}
            alt={image.alt}
            loading="eager"
            fetchPriority="high"
          />
          <StyledContent>
            <StyledHeading>{heading}</StyledHeading>
            <StyledButtons>
              {links.map(({ text, pageUrl }, id) => (
                <StyledButton key={id} to={pageUrl}>
                  {text}
                </StyledButton>
              ))}
            </StyledButtons>
          </StyledContent>
        </StyledPrimary>
        <StyledSecondary>
          {ctas.map((cta, id) => (
            <BannerCta key={id} {...cta} />
          ))}
        </StyledSecondary>
      </StyledInner>
    </Container>
  </StyledBanner>
);

export default Banner;
